//@ts-check
import * as d3 from "d3";
import * as events from './utils/Events'
import * as colorBy from './utils/ColorBy'
import * as visibility from './utils/Visibility'
import * as selection from './utils/Selection'
import * as highLight from './utils/Highlight'
import * as zoom from './utils/Zoom'
import * as label from './utils/Label'
import * as tooltip from './utils/Tooltip'
import * as transform from './utils/Transform'
import * as canvas from './utils/Canvas'
import * as styles from './utils/Styles'
import * as utils from './utils'


// viewer default settings
let _defaultParams = {
  backgroundColor: '#fafafa',
  highlightColor: '#ff00ff',
  backgroundImage: false,
  pickColor: '#72aee6',
  strokeWidth: 0,
  strokeOpacity: 1,
  ghostColor: '#888888',
  filterOpacity: 0.5,
  textColor: '#202020',
  textScale: 0.001,
  hoverWidth: 2,
  hoverColor: 'transparent',
  labelSize: 12,
  labelStrokeColor: '#ffffff',
  labelFillColor: '#ffffff',
  labelTextColor: '#000000',
  labelStrokeWidth: 0,
  tooltipColor: '#000000',
  digits: 4,
};

/**
 * Initiates a new Viewer
 * @class
 */
class Viewer {



  /**
 * Observes changes to the size of the canvas element and adjusts the camera and renderer accordinly
 * @constant 
 * @type {ResizeObserver} 
 */
    resizeObserver;


  /**
 * Adds a 2D Viewer with default or custom viewer settings.
 * Viewer instance used for below functionalities :
 * 1. Register events, single and multiple selection of elements, highlighting elements, clearing selection and highlights of elements, get selected element IDs.
 * 2. Apply custom colors, opacity, isolate elements, lock elements
 * 3. Add and remove labels of elements
 * 4. Show or hide elements or drawing
 * 5. Reset element, color of element
 * 6. Focus or zoom on elements or drawing
 * 7. Clear all the added 2D Viewer
 * 8. Remove the drawing
 * @constructs Viewer
 * @param {{ backgroundColor: any,  pickColor: any,  highlightColor: any,  ghostColor: any,  textColor: any, strokeWidth: number,  strokeOpacity: number,  filterOpacity: number, textScale: number}} initParams Pass the customized settings to apply on 2D viewer. If customization is not passed then default settings will apply on 2D Viewer.
 */
  constructor(initParams) {
    let params = { ..._defaultParams, ...(initParams || {}) };
    this.canvas = null;
    this.multipleSelectionFlagCheck = null;
    this.highlightElementFlagCheck = null;
    this.zoom = null;
    this.backgroundColor = params.backgroundColor;
    this.highlightColor = params.highlightColor;
    this.hoverWidth = params.hoverWidth;
    this.hoverColor = params.hoverColor;
    this.ghostColor = params.ghostColor;
    this.isGhostColor = false;
    this.filterOpacity = params.filterOpacity;
    this.pickColor = params.pickColor;
    this.strokeWidth = params.strokeWidth;
    this.strokeOpacity = params.strokeOpacity;
    this.textColor = params.textColor;
    this.textScale = params.textScale;
    this.boundary = null;
    this.tooltip = null;
    this.tooltipContent = "";
    this.tooltipColor = params.tooltipColor;
    this.temp_pick = [];
    this.temp_highlight = [];
    this.temp_colorBy = [];
    this.arrayElements = [];
    this.labelSize = params.labelSize;
    this.labelFillColor = params.labelFillColor;
    this.digits = params.digits;
    this.id = null;
    this.parent = {};
    this.overlay = {};
    this.interaction = {};
    this.content = {};
    this.types = 'path, polyline, arc, circle, ellipse, line, rect, text';
  }

  /**
  * Add a 2D Viewer instance
  * @param {HTMLDivElement} canvas Pass the HTML Div Element which will act as a 2D Viewer. Passed HTML Div Element width and height must be 100%. 
  */
  addViewer(canvas) {
    let scope = this;
    scope.canvas = canvas;
    scope.updateViewer();
    scope.resizeOberserverInit(); // to trigger resizeOberserverInit
  }

  /**
   * Updates Viewer
   */
  updateViewer() {
    let scope = this;
    d3.select(scope.canvas)
      .attr('width', scope.canvas.offsetWidth)
      .attr('height', scope.canvas.offsetHeight)
      .style('background', scope.backgroundColor);
  }

  /**
   * Resizes the Viewer to maintain the same height and width of the SVG when the window is resized.
   * Adjusts the viewBox to center the model within the canvas.
   */
  resizeOberserverInit() {
    let scope = this;

    function onResize() {
      let svg = d3.select(scope.canvas).select('svg');
      if (!svg.node()) return; // Ensure SVG exists
  
      // Get the bounding box of the SVG content
      const canvasWidth = scope.canvas.offsetWidth;
      const canvasHeight = scope.canvas.offsetHeight;

      svg.attr('width', canvasWidth)
        .attr('height', canvasHeight)
    }

    function throttle(f, delay) {
      let timer = 0;
      return function (...args) {
          clearTimeout(timer);
          timer = setTimeout(() => f.apply(this, args), delay);
      }
    }

    let resizeObserver = new ResizeObserver(throttle(onResize, 100));
    resizeObserver.observe(scope.canvas);
  }

  /**
   * Sets Svg Paramaters
   */
  setSvgParameters() {
    let scope = this;

    scope.setContent(scope.parent);
    scope.setGroupStatus(scope.parent);
    scope.setOverlay(scope.interaction);

    this.boundary = d3.select(scope.content).node()._groups[0][0].viewportElement.getBBox();
  }

  /**
   * Set Group Status
   * @param {*} content 
   */
  setGroupStatus(content) {
    let scope = this;

    var allGroups = content.selectAll('g');
    allGroups.each(function () {

      var elem = d3.select(this);
      scope.content.append(() => elem.node());

      scope.setDefaultGroup(elem);

      var underlay = elem.clone(true);
      scope.interaction.append(() => underlay.node());
      underlay.on("mouseover", function (d) { scope.formatTooltipOn(scope.tooltip) })
      underlay.on("mouseleave", function (d) { scope.formatTooltipOff(scope.tooltip) })
    });
  }

  /**
   * Set default Group
   * @param {*} element 
   */
  setDefaultGroup(element) {

    element.attr('data-isSel', false);
    element.attr('data-isHov', false);
    element.attr('data-isCol', false);
    element.attr('data-color', '');
    element.attr('data-isLbl', false);

  }

  /**
   * Sets Content
   * @param {*} content 
   */
  setContent(content) {
    let scope = this;

    var elements = content.selectAll(scope.types);

    this.tooltip = d3.select(this.canvas).append("div");
    this.formatTooltipDefault(this.tooltip);

    elements.each(function () {

      var elem = d3.select(this);

      let style = getComputedStyle(this);

      var isStroke = true;
      if (style.stroke == 'none') isStroke = false;

      var isFill = true;
      if (style.fill == 'none') isFill = false;

      elem.attr('data-isStroke', isStroke);
      elem.attr('data-isFill', isFill);
    });

  }

  /**
   * Sets Overlay
   * @param {*} content 
   */
  setOverlay(content) {
    let scope = this;

    var elements = content.selectAll(scope.types);

    elements.each(function () {
      var elem = d3.select(this);

      if (elem.attr('data-isStroke') == 'true') elem.style('stroke', scope.hoverColor);
      if (elem.attr('data-isFill') == 'true') elem.style('fill', scope.hoverColor);

      var underlay = elem.clone(true);

      underlay.attr('vector-effect', 'non-scaling-stroke');
      underlay.style('stroke-width', scope.hoverWidth);
    });

  }


  // █████ █   █ █████ █   █ █████ █████ 
  // █     █   █ █     ██  █   █   █     
  // ████   █ █  ████  █ █ █   █   █████ 
  // █      █ █  █     █  ██   █       █ 
  // █████   █   █████ █   █   █   █████ 

  registerEvent = events.registerEvent;
  pickElement = events.pickElement;
  highlightElement = events.highlightElement;


  // █████ █████ █   █ █     █████ █   █  ████ 
  // █       █    █ █  █       █   ██  █ █     
  // █████   █     █   █       █   █ █ █ █  ██ 
  //     █   █     █   █       █   █  ██ █   █ 
  // █████   █     █   █████ █████ █   █  ███  

  updateLineWidth = styles.updateLineWidth;
  updateHoverWidth = styles.updateHoverWidth;
  
  colorGroup = colorBy.colorGroup;
  applyColor = colorBy.applyColor;


  //█▀█ █▀█ ▄▀▄ █▀▀ ▀█▀ ▀█▀ ▀▄▀ 
  //█▄█ █▀▀ █▀█ █▄▄ ▄█▄  █   █  

  groupOpacity = visibility.groupOpacity;
  applyOpacity = visibility.applyOpacity;


  //  ████  ███  █      ███  ████     ████  █   █ 
  // █     █   █ █     █   █ █   █    █   █  █ █  
  // █     █   █ █     █   █ ████     ████    █   
  // █     █   █ █     █   █ █   █    █   █   █   
  //  ████  ███  █████  ███  █   █    ████    █   

  colorElement = colorBy.colorElement;
  colorElements = colorBy.colorElements;
  clearGroupStyle = colorBy.clearGroupStyle;
  clearStyle = colorBy.clearStyle;
  resetElement = colorBy.resetElement;
  resetAllColors = colorBy.resetAllColors;

  setElementOpacity = visibility.setElementOpacity;
  setOpacity = visibility.setOpacity;

  // █████ █████ █     █████  ████ █████
  // █     █     █     █     █       █  
  // █████ ████  █     ████  █       █  
  //     █ █     █     █     █       █  
  // █████ █████ █████ █████  ████   █  

  applySelection = selection.applySelection;
  selectElement = selection.selectElement;
  selectElements = selection.selectElements;
  getSelected = selection.getSelected;
  clear_pickSelection = selection.clear_pickSelection;
  multiSelection = selection.multiSelection;



  // █   █ █████  ████ █   █ █     █████  ████ █   █ █████ 
  // █   █   █   █     █   █ █       █   █     █   █   █   
  // █████   █   █  ██ █████ █       █   █  ██ █████   █   
  // █   █   █   █   █ █   █ █       █   █   █ █   █   █   
  // █   █ █████  ███  █   █ █████ █████  ███  █   █   █   

  applyHighlight = highLight.applyHighlight;
  highlightCheck = highLight.highlightCheck;
  clear_highlightSelection = highLight.clear_highlightSelection;
  highlightedElement = highLight.highlightedElement;
  highlightedElements = highLight.highlightedElements;


  // █      ███   ████ █  █  
  // █     █   █ █     █ █   
  // █     █   █ █     ██ █  
  // █     █   █ █     █  █  
  // █████  ███   ████ █   █ 


  lockElements = visibility.lockElements;
  lockElement = visibility.lockElement;


  // █   █ █████ █████ █████ ████  █████ █     █████ █████ █   █ 
  // █   █   █   █       █   █   █   █   █       █     █    █ █  
  // █████   █   █████   █   ████    █   █       █     █     █   
  // █   █   █       █   █   █   █   █   █       █     █     █   
  // █   █ █████ █████ █████ ████  █████ █████ █████   █     █   


  //█▀▀ █▄█ █▀█ █ █ █   █  █▄█ ▀█▀ █▀▄ █▀▀ 
  //▄██ █ █ █▄█ ▀▄▀▄▀  █   █ █ ▄█▄ █▄▀ ██▄ 

  showElement = visibility.showElement;
  showElements = visibility.showElements;
  showAll = visibility.showAll;


  //▀█▀ █▀▀ █▀█ █   ▄▀▄ ▀█▀ █▀▀ 
  //▄█▄ ▄██ █▄█ █▄▄ █▀█  █  ██▄ 

  isolateElement = visibility.isolateElement;
  isolateElements = visibility.isolateElements;


  //hide background image
  hideBackgroundImage = visibility.hideBackgroundImage;
  showBackgroundImage = visibility.showBackgroundImage;
  toggleBackgroundImage = visibility.toggleBackgroundImage;

  // █████  ███   ███  █   █ 
  //    █  █   █ █   █ ██ ██ 
  //   █   █   █ █   █ █ █ █ 
  //  █    █   █ █   █ █   █ 
  // █████  ███   ███  █   █ 


  multipleModelPosition = zoom.multipleModelPosition;
  zoomExtents = zoom.zoomExtents;
  zoomSelection = zoom.zoomSelection;
  zoomToElements = zoom.zoomToElements;
  zoomMultiple = zoom.zoomMultiple;
  zoomRegion = zoom.zoomRegion;
  getCurrentView = zoom.getCurrentView;
  loadSavedView = zoom.loadSavedView;

  // █      ███  ████  █████ █     
  // █     █   █ █   █ █     █     
  // █     █████ ████  ████  █     
  // █     █   █ █   █ █     █     
  // █████ █   █ ████  █████ █████ 

  labelElement = label.labelElement;
  labelElements = label.labelElements;
  labelStyle = label.labelStyle;
  updateAllLabels = label.updateAllLabels;
  removeLabel = label.removeLabel;
  removeLabels = label.removeLabels;
  clearAllLabels = label.clearAllLabels;


  // █████  ███   ███  █     █████ █████ ████  
  //   █   █   █ █   █ █       █     █   █   █ 
  //   █   █   █ █   █ █       █     █   ████  
  //   █   █   █ █   █ █       █     █   █     
  //   █    ███   ███  █████   █   █████ █     

  setTooltipContent = tooltip.setTooltipContent;
  applyToolTip = tooltip.applyToolTip;
  formatTooltipMove = tooltip.formatTooltipMove;
  formatTooltipDefault = tooltip.formatTooltipDefault;
  formatTooltipOn = tooltip.formatTooltipOn;
  formatTooltipOff = tooltip.formatTooltipOff


  // █████ ████   ███  █   █ █████ █████  ███  ████  █   █ 
  //   █   █   █ █   █ ██  █ █     █     █   █ █   █ ██ ██ 
  //   █   ████  █████ █ █ █ █████ ████  █   █ ████  █ █ █ 
  //   █   █   █ █   █ █  ██     █ █     █   █ █   █ █   █ 
  //   █   █   █ █   █ █   █ █████ █      ███  █   █ █   █ 

  translateDrawing = transform.translateDrawing;
  rotateDrawing = transform.rotateDrawing;
  scaleDrawing = transform.scaleDrawing;


  //  ████  ███  █   █ █   █  ███  █████ 
  // █     █   █ ██  █ █   █ █   █ █     
  // █     █████ █ █ █  █ █  █████ █████ 
  // █     █   █ █  ██  █ █  █   █     █ 
  //  ████ █   █ █   █   █   █   █ █████ 

  setBackgroundColor = canvas.setBackgroundColor;
  resetAll = canvas.resetAll;
  clearAll = canvas.clearAll;
  removeDrawing = canvas.removeDrawing;

  // █   █ █████ █████ █     █████ █████ █████ █████ █████ 
  // █   █   █     █   █       █     █     █   █     █     
  // █   █   █     █   █       █     █     █   ████  █████ 
  // █   █   █     █   █       █     █     █   █         █ 
  // █████   █   █████ █████ █████   █   █████ █████ █████ 

  existsInArrayWhile = utils.existsInArrayWhile;
  toSvgString = utils.toSvgString;
  setDigits = utils.setDigits;

  // █████ █████ █     █████ █████ ████     █████ █████ █████ █████  ████ █████ █████
  // █       █   █     █       █   █   █    █     █     █     █     █       █     █  
  // ████    █   █     ████    █   ████     █████ ████  █     █████  ████   █     █  
  // █       █   █     █       █   █   █        █ █     █     █          █  █     █  
  // █     █████ █████ █████ █████ █   █    █████ █     █████ █████  ████   █     █  

  // Add new filter effect methods
  fadeElements = visibility.fadeElements;
  ghostElements = visibility.ghostElements;
  restoreElements = visibility.restoreElements;
  removeFilterEffects = visibility.removeFilterEffects;

    /**
   * Clean up the instance
   * @name destroy
   */
    destroy() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }
}
export { Viewer };
